import { ExternalLink } from "lucide-react";
import { Button } from "../../../components/ui/button";
import { Separator } from "../../../components/ui/separator";
import { ReactNode } from "react";
import { OfferYacht, OfferYachtDefault } from "../model";
import { YachtDetails } from "../../Yachts/model";
import { Currency } from "../../Currencies/model";
import { formatDateUTC } from "@/src/helpers/formatDateUTC";
import { getConfig } from "@/src/config";
import { extractFromMultiName } from "../../Locations/utils";
import YachtLabel from "@/src/components/ui/yacht-label";
import { ReactionChip } from "../EngagementTimeline";

type YachtOfferCard = OfferYachtDefault & {
  header?: ReactNode;
  openYachtDetails?: () => void;
  onAddClick?: (data: OfferYacht) => void;
  yacht: YachtDetails;
  currency?: Currency["code"];
  openChangeDetails?: (id: string) => void;
  reaction?: "love" | "maybe" | "pass" | null;
  reactionNote?: string | null;
  holdExpiresAt?: string | null;
};

export const YachtOfferCard = (props: YachtOfferCard) => {
  const includes = [...props.yacht.amenities]?.filter((am) => am.included);
  const obligatories = [...props.yacht.amenities]?.filter((am) => am.obligatory);
  const startDate = new Date(props.startDate);
  const endDate = new Date(props.endDate);

  // Helper to get the website URL from runtime config
  function getWebsiteUrl(): string {
    try {
      const { WEBSITE_URL } = getConfig();
      return WEBSITE_URL;
    } catch (err) {
      console.error("Config not loaded yet", err);
      return ""; // fallback or throw error if preferred
    }
  }
  return (
    <>
      {props.header}
      <div className="flex gap-x-6">
        <img
          width={250}
          height={150}
          className="h-[150px] object-cover rounded-[5px]"
          src={props.yacht?.media?.[0]}
          alt={props.yacht?.name}
        />
        <div className="flex flex-col items-start">
          <div className="flex items-center gap-2 flex-wrap">
            <YachtLabel apiDateUpdated={props.yacht?.apiDateUpdated} apiStatus={props.yacht?.apiStatus} />
            <ReactionChip reaction={props.reaction} note={props.reactionNote} />
            {props.holdExpiresAt && (
              <span className="inline-flex items-center rounded-full bg-teal-50 border border-teal-200 text-teal-800 px-2.5 py-0.5 text-xs font-medium">
                Held until {new Date(props.holdExpiresAt).toLocaleDateString("en-GB", { day: "numeric", month: "short" })}
              </span>
            )}
          </div>
          <Button asChild className="text-black p-0 h-[28px]" variant="link">
            <a target="_blank" rel="noreferrer" href={`${getWebsiteUrl()}/yacht/${props.yacht?.slug}`}>
              <span className="max-w-[400px] overflow-hidden text-ellipsis"> “{props.yacht?.name}”</span>
              <ExternalLink className="ml-1.5" size={16} />
            </a>
          </Button>
          <span className="text-base mt-1.5">
            {props.yacht?.modelName} | {props.yacht?.year}
          </span>
          <span className="text-base mt-1.5">
            {!!props.yacht?.maxPeople && `${props.yacht?.maxPeople} people`}{" "}
            {!!props.yacht?.cabins && `• ${props.yacht?.cabins} cabins`}{" "}
            {!!props.yacht?.bathrooms && `• ${props.yacht?.bathrooms} bathrooms`}
          </span>
          <div className="flex flex-col mt-2">
            <span className="text-xs text-slate-500 font-medium">Operator</span>
            <span>
              {props.yacht?.operator?.name} | {`(${props.yacht?.operator?.rating}/10)`}
            </span>
          </div>
          <div className="flex flex-col mt-2">
            <span className="text-xs text-slate-500 font-medium">Brand</span>
            <span className="text-sm">
              {[props.yacht?.visibleInAnchor && "Anchor", props.yacht?.visibleInHelm && "Helm"]
                .filter(Boolean)
                .join(", ")}
            </span>
          </div>
        </div>
        <div className="flex flex-col justify-between ml-auto">
          <span className="flex ml-auto items-end text-xs">
            <span className="text-xl mx-1.5">
              {new Intl.NumberFormat("en-EN", { style: "currency", currency: props.currency }).format(
                props.pricing?.totalPrice,
              )}
            </span>
          </span>
          <div className="flex items-center gap-x-4 justify-end">
            <>
              {props.openChangeDetails && (
                <Button
                  onClick={(e) => {
                    e.preventDefault();
                    props.openChangeDetails?.(props.yacht?.id);
                  }}
                  variant="secondary"
                >
                  Edit
                </Button>
              )}
              <Button
                onClick={(e) => {
                  e.preventDefault();
                  props.openYachtDetails?.();
                }}
                variant="secondary"
              >
                Yacht details
              </Button>
            </>
          </div>
        </div>
      </div>
      <Separator className="my-4" />
      <div className="flex">
        <div className="flex flex-col gap-y-8">
          <div className="flex gap-x-8">
            <div className="flex flex-col w-[186px]">
              <span className="text-xs text-slate-500 font-medium">Check-in</span>
              <span className="text-base font-medium">{extractFromMultiName(props.startLocation?.names)}</span>
            </div>
            <div className="flex flex-col w-[186px]">
              <span className="text-xs text-slate-500 font-medium">Check-out</span>
              <span className="text-base font-medium">{extractFromMultiName(props.endLocation?.names)}</span>
            </div>
          </div>
          <div className="flex gap-x-8">
            <div className="flex flex-col w-[186px]">
              <span className="text-xs text-slate-500 font-medium">Start date</span>
              <span className="text-base font-medium">{formatDateUTC(props.startDate, "UTC")}</span>
            </div>
            <div className="flex flex-col w-[186px]">
              <span className="text-xs text-slate-500 font-medium">End date</span>
              <span className="text-base font-medium">{formatDateUTC(props.endDate, "UTC")}</span>
            </div>
          </div>
        </div>
        <Separator orientation="vertical" className="mx-6 h-auto" />
        <div className="flex flex-col gap-y-4">
          <div className="flex flex-col gap-y-1.5">
            <span className="text-xs text-slate-500 font-medium">Main features</span>
            <ul className="flex items-center flex-wrap gap-1.5">
              {includes?.slice(0, 5)?.map((feature, i) => (
                <li key={i} className="leading-3 rounded-[25px] py-1.5 px-3 bg-slate-100 font-medium text-sm">
                  {feature.text}
                </li>
              ))}
              {!!includes?.slice(5).length && (
                <li className="leading-3 rounded-[25px] py-1.5 px-3 bg-slate-100 font-medium text-sm">
                  + {includes?.slice(5)?.length}
                </li>
              )}
            </ul>
          </div>
          <div className="flex flex-col gap-y-1.5">
            <span className="text-xs text-slate-500 font-medium">Obligatory extras</span>
            <ul className="flex items-center flex-wrap gap-1.5">
              {obligatories?.slice(0, 5).map((feature, i) => (
                <li key={i} className="leading-3 rounded-[25px] py-1.5 px-3 bg-slate-100 font-medium text-sm">
                  {feature.text}
                </li>
              ))}
              {!!obligatories?.slice(5).length && (
                <li className="leading-3 rounded-[25px] py-1.5 px-3 bg-slate-100 font-medium text-sm">
                  + {obligatories?.slice(5)?.length}
                </li>
              )}
            </ul>
          </div>
        </div>
      </div>
    </>
  );
};
